'use client';

import { useMemo } from 'react';
import { STABLE_COIN_SYMBOLS } from 'src/constants/constants';
import { useQuery } from '@tanstack/react-query';
import { PublicKey } from '@velocity-exchange/sdk';
import { createQueryKey } from 'src/utils/query/createQueryKey';
import { GET_API_ROUTE } from 'src/constants/api';
import { HistoricalTokenPriceDuration } from 'src/utils/graph';
import { getRetryDelayFn } from 'src/utils/query/retryDelay';

interface SimpleTokenData {
	[id: string]: {
		usd_24h_vol: number;
		usd_24h_change: number;
		usd: number;
	};
}

type OpenHighLowClose = [
	ms: number,
	open: number,
	high: number,
	low: number,
	close: number,
];

interface MarketChartData {
	prices: [ms: number, price: number][];
}

interface JupiterPriceApi {
	[key: string]: {
		id: string;
		mintSymbol: string;
		usdPrice: string;
		decimals: number;
		priceChange24h: number;
	};
}

const queryOptions = {
	retryDelay: getRetryDelayFn(20_000),
	refetchOnWindowFocus: false,
	staleTime: 60_000,
};

const DEFAULT_TOKEN_PAIR_DATA = {
	tokenPairPrice: 0,
	low24h: 0,
	high24h: 0,
	isLoading: false,
	fromTokenPrice: 0,
	toTokenPrice: 0,
};

// CoinGecko IDs for frequently used tokens; symbols are Velocity spot market config symbols
const FREQUENTLY_USED_TOKEN_SYMBOLS = [
	{ symbol: 'sol', id: 'solana' },
	{ symbol: 'usdt', id: 'tether' },
	{ symbol: 'wbtc', id: 'wrapped-btc-wormhole' },
	{ symbol: 'weth', id: 'weth' },
	{ symbol: 'zec', id: 'zcash' },
];

const JUPITER_PRICE_API = 'https://lite-api.jup.ag/price/v3';

const useCoingeckoTokenPairIds = (
	fromTokenSymbol: string,
	toTokenSymbol: string
) => {
	const fromTokenId = FREQUENTLY_USED_TOKEN_SYMBOLS.find(
		(coin) => coin.symbol.toLowerCase() === fromTokenSymbol?.toLowerCase()
	)?.id;
	const toTokenId = FREQUENTLY_USED_TOKEN_SYMBOLS.find(
		(coin) => coin.symbol.toLowerCase() === toTokenSymbol?.toLowerCase()
	)?.id;

	return {
		fromTokenId,
		toTokenId,
	};
};

// Uses CoinGecko API to fetch token prices.
// If either of the token involved is USDC, the price will be denominated in USDC
export const useTokenPairData = (
	fromTokenSymbol: string,
	toTokenSymbol: string
) => {
	const { fromTokenId, toTokenId } = useCoingeckoTokenPairIds(
		fromTokenSymbol,
		toTokenSymbol
	);

	// fetch tokens prices
	const {
		data: tokensData,
		error: tokensDataError,
		isLoading: isTokenPriceLoading,
	} = useQuery<SimpleTokenData>({
		queryKey: createQueryKey('coingecko-simple-price', fromTokenId, toTokenId),
		queryFn: () =>
			fetch(
				`${GET_API_ROUTE('coingecko-simple-price')}?${new URLSearchParams({
					tokens: [fromTokenId!, toTokenId!].join(','),
				})}`
			).then((r) => {
				if (!r.ok) throw new Error(`HTTP ${r.status}`);
				return r.json();
			}),
		enabled: !!fromTokenId && !!toTokenId,
		...queryOptions,
	});

	const fromTokenData = tokensData?.[fromTokenId ?? ''];
	const toTokenData = tokensData?.[toTokenId ?? ''];

	// fetch open, high, low, close prices for the last 24 hours at 30 mins intervals
	const {
		data: from24hOhlc,
		error: fromOhlcError,
		isLoading: isFromOhlcLoading,
	} = useQuery<OpenHighLowClose[]>({
		queryKey: createQueryKey('coingecko-ohlc', fromTokenId),
		queryFn: () =>
			fetch(
				`${GET_API_ROUTE('coingecko-ohlc')}?${new URLSearchParams({
					tokenId: fromTokenId!,
				})}`
			).then((r) => {
				if (!r.ok) throw new Error(`HTTP ${r.status}`);
				return r.json();
			}),
		enabled: !!fromTokenId,
		...queryOptions,
	});
	const {
		data: to24hOhlc,
		error: toOhlcError,
		isLoading: isToOhlcLoading,
	} = useQuery<OpenHighLowClose[]>({
		queryKey: createQueryKey('coingecko-ohlc', toTokenId),
		queryFn: () =>
			fetch(
				`${GET_API_ROUTE('coingecko-ohlc')}?${new URLSearchParams({
					tokenId: toTokenId!,
				})}`
			).then((r) => {
				if (!r.ok) throw new Error(`HTTP ${r.status}`);
				return r.json();
			}),
		enabled: !!toTokenId,
		...queryOptions,
	});

	const isLoading = isTokenPriceLoading || isFromOhlcLoading || isToOhlcLoading;

	// denominate by stable coin if fromToken is stable coin
	const tokenPairPrice = STABLE_COIN_SYMBOLS.includes(
		fromTokenSymbol?.toLowerCase()
	)
		? (toTokenData?.usd ?? 0) / (fromTokenData?.usd || 1)
		: (fromTokenData?.usd ?? 0) / (toTokenData?.usd || 1);

	const low24h =
		from24hOhlc && from24hOhlc instanceof Array
			? from24hOhlc.reduce((min, fromOhlc) => {
					const timeInterval = fromOhlc[0];
					const fromTokenLowPrice = fromOhlc[3];

					const toOhlc =
						to24hOhlc && to24hOhlc instanceof Array
							? to24hOhlc.find((matchedOhlc) => matchedOhlc[0] === timeInterval)
							: 0;
					const toTokenLowPrice =
						toOhlc && toOhlc instanceof Array ? toOhlc[3] : 0;

					const timeIntervalLow = STABLE_COIN_SYMBOLS.includes(
						fromTokenSymbol?.toLowerCase()
					)
						? (toTokenLowPrice ?? 0) / (fromTokenLowPrice || 1)
						: (fromTokenLowPrice ?? 0) / (toTokenLowPrice || 1);

					return Math.min(min, timeIntervalLow);
				}, Infinity)
			: 0;

	const high24h =
		from24hOhlc && from24hOhlc instanceof Array
			? from24hOhlc.reduce((max, fromOhlc) => {
					const timeInterval = fromOhlc[0];
					const fromTokenHighPrice = fromOhlc[2];

					const toOhlc =
						to24hOhlc && to24hOhlc instanceof Array
							? to24hOhlc.find((matchedOhlc) => matchedOhlc[0] === timeInterval)
							: 0;
					const toTokenHighPrice =
						toOhlc && toOhlc instanceof Array ? toOhlc[2] : 0;

					const timeIntervalHigh = STABLE_COIN_SYMBOLS.includes(
						fromTokenSymbol?.toLowerCase()
					)
						? (toTokenHighPrice ?? 0) / (fromTokenHighPrice || 1)
						: (fromTokenHighPrice ?? 0) / (toTokenHighPrice || 1);

					return Math.max(max, timeIntervalHigh);
				}, 0)
			: 0;

	if (!fromTokenId || !toTokenId) {
		return DEFAULT_TOKEN_PAIR_DATA;
	}

	// default data if any error occurs
	if (tokensDataError || fromOhlcError || toOhlcError) {
		console.error(tokensDataError || fromOhlcError || toOhlcError);
		return DEFAULT_TOKEN_PAIR_DATA;
	}

	return {
		tokenPairPrice,
		low24h,
		high24h,
		isLoading,
		fromTokenPrice: fromTokenData?.usd ?? 0,
		toTokenPrice: toTokenData?.usd ?? 0,
	};
};

export const useJupiterTokenPairData = (
	swapFromMarket: {
		mint: PublicKey | string;
		symbol?: string;
	},
	swapToMarket: {
		mint: PublicKey | string;
	}
) => {
	const swapFromMintString = useMemo(() => {
		return swapFromMarket?.mint?.toString();
	}, [swapFromMarket?.mint]);
	const swapToMintString = useMemo(() => {
		return swapToMarket?.mint?.toString();
	}, [swapToMarket?.mint]);

	const { data } = useQuery<JupiterPriceApi>({
		queryKey: createQueryKey(
			'jupiter-price',
			swapFromMintString,
			swapToMintString
		),
		queryFn: () =>
			fetch(
				`${JUPITER_PRICE_API}?ids=${swapFromMintString},${swapToMintString}`
			).then((r) => {
				if (!r.ok) throw new Error(`HTTP ${r.status}`);
				return r.json();
			}),
		enabled: !!swapFromMintString && !!swapToMintString,
		...queryOptions,
	});

	const swapFromPrice = +(data?.[swapFromMintString]?.usdPrice ?? 0);
	const swapToPrice = +(data?.[swapToMintString]?.usdPrice ?? 0);

	let price = swapFromPrice / swapToPrice;

	price =
		price &&
		swapFromMarket.symbol &&
		STABLE_COIN_SYMBOLS.includes(swapFromMarket.symbol)
			? 1 / (+price || 1)
			: price;

	return price;
};

/**
 * Fetches both tokens' historical prices and returns the denominated prices.
 * It will denominate the prices by USDC/T if USDC/T is one of the tokens.
 * 1 day = ~5 mins interval between prices
 * > 1 day = ~1 hour interval between prices
 */
export const useTokenPairHistoricalPrices = (
	fromTokenSymbol: string,
	toTokenSymbol: string,
	duration: HistoricalTokenPriceDuration
) => {
	const { fromTokenId, toTokenId } = useCoingeckoTokenPairIds(
		fromTokenSymbol,
		toTokenSymbol
	);

	// fetch token historical prices
	const { data: fromTokenHistoricalPrices, error: fromHistoricalPricesError } =
		useQuery<MarketChartData>({
			queryKey: createQueryKey('coingecko-history', fromTokenId, duration),
			queryFn: () =>
				fetch(
					`${GET_API_ROUTE('coingecko-history')}?${new URLSearchParams({
						tokenId: fromTokenId!,
						duration,
					})}`
				).then((r) => {
					if (!r.ok) throw new Error(`HTTP ${r.status}`);
					return r.json();
				}),
			enabled: !!fromTokenId,
			...queryOptions,
		});
	const { data: toTokenHistoricalPrices, error: toHistoricalPricesError } =
		useQuery<MarketChartData>({
			queryKey: createQueryKey('coingecko-history', toTokenId, duration),
			queryFn: () =>
				fetch(
					`${GET_API_ROUTE('coingecko-history')}?${new URLSearchParams({
						tokenId: toTokenId!,
						duration,
					})}`
				).then((r) => {
					if (!r.ok) throw new Error(`HTTP ${r.status}`);
					return r.json();
				}),
			enabled: !!toTokenId,
			...queryOptions,
		});

	// fetch current price
	const { data: tokensData, error: tokensDataError } =
		useQuery<SimpleTokenData>({
			queryKey: createQueryKey(
				'coingecko-simple-price',
				fromTokenId,
				toTokenId
			),
			queryFn: () =>
				fetch(
					`${GET_API_ROUTE('coingecko-simple-price')}?${new URLSearchParams({
						tokens: [fromTokenId!, toTokenId!].join(','),
					})}`
				).then((r) => {
					if (!r.ok) throw new Error(`HTTP ${r.status}`);
					return r.json();
				}),
			enabled: !!fromTokenId && !!toTokenId,
			...queryOptions,
		});

	// use current price as last historical price
	const fromLastIndex = (fromTokenHistoricalPrices?.prices?.length ?? 0) - 1;
	const toLastIndex = (toTokenHistoricalPrices?.prices?.length ?? 0) - 1;
	if (fromLastIndex >= 0 && fromTokenHistoricalPrices?.prices[fromLastIndex]) {
		fromTokenHistoricalPrices.prices[fromLastIndex] = [
			fromTokenHistoricalPrices?.prices[fromLastIndex][0],
			tokensData?.[fromTokenId ?? '']?.usd ?? 0,
		];
	}
	if (toLastIndex >= 0 && toTokenHistoricalPrices?.prices[toLastIndex]) {
		toTokenHistoricalPrices.prices[toLastIndex] = [
			toTokenHistoricalPrices?.prices[toLastIndex][0],
			tokensData?.[toTokenId ?? '']?.usd ?? 0,
		];
	}

	const interval = duration === HistoricalTokenPriceDuration.ONE_DAY ? 5 : 60;

	const roundedFromTokenHistoricalPrices =
		fromTokenHistoricalPrices?.prices?.map(([ms, price]) => ({
			ms: Math.round(ms / (interval * 60 * 1000)) * (interval * 60 * 1000),
			price,
		})) ?? [];
	const uniqueFromTokenHistoricalPrices =
		roundedFromTokenHistoricalPrices.filter(({ ms }, index) => {
			if (index === 0) return true;
			return ms !== roundedFromTokenHistoricalPrices[index - 1].ms;
		});

	const roundedToTokenHistoricalPrices =
		toTokenHistoricalPrices?.prices?.map(([ms, price]) => ({
			ms: Math.round(ms / (interval * 60 * 1000)) * (interval * 60 * 1000),
			price,
		})) ?? [];
	const uniqueToTokenHistoricalPrices = roundedToTokenHistoricalPrices.filter(
		({ ms }, index) => {
			if (index === 0) return true;
			return ms !== roundedToTokenHistoricalPrices[index - 1].ms;
		}
	);

	const historicalPrices = useMemo(
		() =>
			uniqueFromTokenHistoricalPrices.reduce(
				(acc, price) => {
					const toTokenPrice = uniqueToTokenHistoricalPrices.find(
						(toPrice) => toPrice.ms === price.ms
					)?.price;

					if (!toTokenPrice) return acc;

					let tokenPairPrice = price.price / toTokenPrice;

					// always denominate by USDC if it is one of the tokens
					if (STABLE_COIN_SYMBOLS.includes(fromTokenSymbol.toLowerCase())) {
						tokenPairPrice = 1 / tokenPairPrice;
					}

					return [...acc, { ms: price.ms, price: tokenPairPrice }];
				},
				[] as Array<{ ms: number; price: number }>
			),
		[fromTokenHistoricalPrices, toTokenHistoricalPrices, tokensData]
	);

	if (!fromTokenId || !toTokenId) {
		return [];
	}

	// default data if any error occurs
	if (fromHistoricalPricesError || toHistoricalPricesError || tokensDataError) {
		console.error(
			fromHistoricalPricesError || toHistoricalPricesError || tokensDataError
		);
		return [];
	}

	return historicalPrices;
};
